import { AuthenticationResult, EventMessage, EventType, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageItems } from 'shared/constants';
import { useAppDispatch, useAppSelector } from 'shared/hooks';

import { tryGetErrorCode } from '../helpers';
import { setError, setStatus } from '../store/authSlice';
import { AuthStatus } from '../types';

const eventToStatusMap: Partial<Record<EventType, AuthStatus>> = {
    [EventType.LOGIN_START]: 'pending',
    [EventType.ACQUIRE_TOKEN_START]: 'pendingSilent',
    [EventType.ACQUIRE_TOKEN_SUCCESS]: 'authenticated',
    [EventType.LOGIN_FAILURE]: 'unauthorized',
    [EventType.LOGOUT_SUCCESS]: 'unauthorized',
    [EventType.ACQUIRE_TOKEN_FAILURE]: 'unauthorized'
};

const successAuthEventTypes: EventType[] = [
    EventType.LOGIN_SUCCESS,
    EventType.ACQUIRE_TOKEN_SUCCESS,
    EventType.SSO_SILENT_SUCCESS,
    EventType.HANDLE_REDIRECT_END
];

export default function useActualizeLoadingStatus(instance: IPublicClientApplication, inProgress: InteractionStatus) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('auth');
    const status = useAppSelector((state) => state.auth.status);
    const accessToken = useAppSelector((state) => state.auth.accessToken);

    useEffect(() => {
        if (status === 'idle' && inProgress === 'none' && !accessToken.token) {
            dispatch(setStatus('unauthorized'));
        }
    }, [ inProgress, status, dispatch, accessToken.fullAccess, accessToken.token ]);

    useEffect(() => {
        function preserveStatus(event: EventMessage) {
            const status = eventToStatusMap[event.eventType];

            if (status) {
                dispatch(setStatus(status));
            }
        }

        function preserveError(event: EventMessage) {
            if (event.eventType === EventType.LOGIN_FAILURE) {
                const error = event?.error;

                const errorCode = tryGetErrorCode(error);
                dispatch(setError({
                    message: t('loginError', { context: errorCode }),
                    errorCode
                }));       
            }
        }

        function preserveExpiration(event: EventMessage) {
            const clearTokenEvents: EventType[] = [
                EventType.LOGOUT_SUCCESS,
                EventType.LOGIN_FAILURE,
                EventType.ACQUIRE_TOKEN_FAILURE
            ];

            if (clearTokenEvents.includes(event.eventType)) {
                localStorage.removeItem(LocalStorageItems.TOKEN_EXPIRES_ON);

                return;
            }

            if (!successAuthEventTypes.includes(event.eventType)) {
                return;
            }

            const result = event.payload as AuthenticationResult;
            const expiresOn = result?.expiresOn instanceof Date ? result.expiresOn.getTime() : undefined;

            if (expiresOn) {
                localStorage.setItem(LocalStorageItems.TOKEN_EXPIRES_ON, expiresOn.toString());
            }
        }

        const callbackId = instance.addEventCallback((event) => {
            preserveStatus(event);
            preserveError(event);
            preserveExpiration(event);
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [ instance, dispatch, t ]);
}
